import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { labelClass } from "./fieldStyles";
import type { DocumentRef } from "../../types/form";
import { useFormStore } from "../../store/formStore";
import { getApi } from "../../api";
import { saveCurrentDraft } from "../../services/saveDraft";
import { trackWarning } from "../../telemetry/appInsights";
import { ACCEPTED_EXTENSIONS, MAX_SIZE_MB, MAX_SIZE_BYTES, formatFileSize } from "./fileUploadConstants";

interface FileUploadProps {
  docType: string;
  label: string;
  currentFile: DocumentRef | null;
  onUpload: (ref: DocumentRef) => void;
  onDelete: () => void;
  required?: boolean;
  hint?: string;
  /** Where to render the hint text. Default "below" renders after the drop zone. "above" renders between the label and the drop zone. */
  hintPosition?: "above" | "below";
  error?: string | null;
  touchAllCounter?: number;
  /**
   * Per-instance file-type allowlist. Lowercase extensions without leading dot
   * (e.g. ["pdf", "jpg", "jpeg", "png"]). Defaults to the shared
   * ACCEPTED_EXTENSIONS list. Overridden when a story restricts a particular
   * upload slot to a narrower set (e.g. binder check is image+PDF only per the
   * 3352 spec, whereas census uploads accept xlsx/csv too).
   */
  acceptExtensions?: readonly string[];
}

/**
 * Single-file upload with drag-and-drop. Calls the API to upload/delete files.
 * Shows file name + size when uploaded, drop zone when empty.
 */
export function FileUpload({
  docType,
  label,
  currentFile,
  onUpload,
  onDelete,
  required,
  hint,
  hintPosition = "below",
  error,
  touchAllCounter = 0,
  acceptExtensions,
}: FileUploadProps) {
  const formId = useFormStore((s) => s.formId);

  // Resolve the effective accept list once per render. The dotted `accept`
  // attribute and the human-readable hint/error are both derived from the
  // same source so they can never drift from the actual validation.
  const exts = acceptExtensions ?? ACCEPTED_EXTENSIONS;
  const acceptAttr = exts.map((e) => `.${e}`).join(",");
  const acceptLabel = exts.map((e) => e.toUpperCase()).join(", ");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slowUpload, setSlowUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => { if (slowTimerRef.current) clearTimeout(slowTimerRef.current); }, []);

  // Two distinct error sources can fire at once on the same field (e.g. user
  // drops a .txt while the field is also required-and-empty): uploadError tells
  // them *why* the file was rejected, while validationError tells them the
  // field still needs a valid file. We stack both rather than picking a winner
  // so the user can see what they did wrong AND what to do next.
  const validationError = (touched || touchAllCounter > 0) ? error : null;
  const hasError = !!(uploadError || validationError);

  const handleFile = useCallback(async (file: File) => {
    if (!formId) { setUploadError("Form not loaded"); return; }
    if (file.size === 0) { setUploadError("File is empty"); return; }
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError(`File exceeds ${MAX_SIZE_MB}MB limit`);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!exts.includes(ext)) {
      setUploadError(`Accepted file types: ${acceptLabel}`);
      return;
    }
    setUploadError(null);
    setUploading(true);
    setSlowUpload(false);
    if (file.size > 5 * 1024 * 1024)
      slowTimerRef.current = setTimeout(() => setSlowUpload(true), 20_000);
    try {
      const api = getApi();
      // Persist the form (incl. the field that makes this upload control visible)
      // before the file is written to Salesforce. The server's orphaned-file cleanup
      // judges visibility off the *persisted* form state, so uploading before that
      // state is saved would let a later read/save detach the fresh file.
      if (!(await saveCurrentDraft())) {
        setUploadError("Couldn't save the form before uploading. Please try again.");
        return;
      }
      const result = await api.uploadFile(formId, docType, file);
      onUpload(result);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      clearTimeout(slowTimerRef.current);
      setUploading(false);
      setSlowUpload(false);
    }
  }, [formId, docType, onUpload, exts, acceptLabel]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    setTouched(true);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }, [handleFile, uploading]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (uploading) return; // mirrors handleDrop — block concurrent uploads on the same field
    setTouched(true);
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    if (inputRef.current) inputRef.current.value = "";
  }, [handleFile, uploading]);

  const handleDelete = useCallback(async () => {
    if (!currentFile) return;
    setDeleting(true);
    try {
      const api = getApi();
      await api.deleteFile(currentFile.fileId);
      onDelete();
    } catch {
      trackWarning("FileUpload: delete failed", { fileId: currentFile.fileId, docType });
      setUploadError("Failed to remove file. Please try again.");
    }
    setDeleting(false);
  }, [currentFile, onDelete, docType]);

  if (currentFile) {
    return (
      <div className="mt-3">
        <span className={labelClass()}>
          {label}
          {required && <span className="text-danger ml-0.5" aria-hidden="true">*</span>}
        </span>
        {hint && hintPosition === "above" && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        <div className={`flex items-center gap-3 border rounded-lg px-4 py-3 mt-1 transition-opacity ${deleting ? "bg-gray-100 border-gray-200 opacity-50" : "bg-gray-50 border-gray-200"}`}>
          {deleting ? (
            <Loader2 size={20} className="text-gray-400 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <FileText size={20} className="text-gray-400 shrink-0" aria-hidden="true" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{currentFile.name}</p>
            <p className="text-xs text-gray-500">{deleting ? "Removing..." : `${formatFileSize(currentFile.size)}`}</p>
          </div>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleting}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-danger transition-colors cursor-pointer shrink-0 disabled:opacity-30 disabled:cursor-default"
            aria-label={`Remove ${currentFile.name}`}
          >
            <X size={18} />
          </button>
        </div>
        {hint && hintPosition === "below" && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
      </div>
    );
  }

  return (
    <div className="mt-3">
      <span className={labelClass()}>
        {label}
        {required && <span className="text-danger ml-0.5" aria-hidden="true">*</span>}
      </span>
      {hint && hintPosition === "above" && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      <label
        aria-label={`Upload ${label}`}
        className={`flex flex-col items-center gap-2 border-2 border-dashed rounded-lg px-4 py-6 mt-1 cursor-pointer transition-all active:scale-[0.98] focus-within:ring-2 focus-within:ring-brand-green-50 focus-within:ring-offset-2 ${
          dragOver
            ? "border-brand-green bg-brand-green-05"
            : hasError
              ? "border-danger bg-danger-bg"
              : "border-gray-300 hover:border-brand-green hover:bg-brand-green-05"
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={acceptAttr}
          onChange={handleInputChange}
          disabled={uploading}
          aria-required={required || undefined}
          aria-invalid={!!error || undefined}
        />
        {uploading ? (
          <Loader2 size={24} className="text-brand-green animate-spin" aria-hidden="true" />
        ) : (
          <Upload size={24} className="text-gray-400" aria-hidden="true" />
        )}
        <span className="text-sm font-medium text-gray-600" aria-live="polite">
          {uploading
            ? slowUpload
              ? "This may take a few minutes depending on your file size. Please wait..."
              : "Uploading..."
            : "Tap to upload or drag & drop"}
        </span>
        <span className="text-xs text-gray-500">{acceptLabel} — Max {MAX_SIZE_MB}MB</span>
      </label>
      {hint && hintPosition === "below" && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
      {uploadError && <p role="alert" className="text-xs text-danger mt-1">{uploadError}</p>}
      {validationError && <p role="alert" className="text-xs text-danger mt-1">{validationError}</p>}
    </div>
  );
}
